import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Container,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';

interface NotFoundProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  customAction?: {
    label: string;
    onClick: () => void;
  };
}

const NotFound: React.FC<NotFoundProps> = ({
  title = "404 - Page Not Found",
  message = "The page you're looking for doesn't exist or has been moved.",
  showHomeButton = true,
  customAction
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <Container maxWidth="md">
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="60vh"
        textAlign="center"
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 6, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <ErrorOutlineIcon 
            sx={{ 
              fontSize: 120, 
              color: theme.palette.error.main,
              mb: 2,
              opacity: 0.8
            }} 
          />
          
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              fontSize: { xs: '2rem', sm: '3rem' }
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="h6" 
            color="textSecondary" 
            paragraph
            sx={{ mb: 4, maxWidth: 500 }}
          >
            {message}
          </Typography>

          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            {showHomeButton && (
              <Button
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                Go to Homepage
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="large"
              onClick={handleGoBack}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              Go Back
            </Button>

            {customAction && (
              <Button
                variant="contained"
                size="large"
                onClick={customAction.onClick}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                {customAction.label}
              </Button>
            )}
          </Box>
        </Paper>

        {/* Additional helpful information */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            If you believe this is an error, please contact support or try refreshing the page.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default NotFound;
